// src/pages/customer/MyOrders.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrders } from '../../contexts/OrdersContext';
import {
    Package,
    Truck,
    CheckCircle,
    XCircle,
    Search,
    RefreshCw,
    MapPin,
    ChevronRight,
    ShoppingCart,
    Clock
} from 'lucide-react';
import { formatFullDate, formatPrice } from '../../utils/utilityFunctions';
import type { OrderItem } from '../../types';

const MyOrders: React.FC = () => {
    const navigate = useNavigate();
    const { orders, isLoading, error, fetchOrders } = useOrders();
    const [searchTerm, setSearchTerm] = useState('');

    // Filter orders by order number or product title
    const filteredOrders = orders
        .filter(order => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            // Search by order number
            if (order.order_number.toLowerCase().includes(term)) return true;
            // Search by product title
            if (order.items?.some(item =>
                item.product_name?.toLowerCase().includes(term)
            )) return true;
            return false;
        })
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const getStatusConfig = (status: string) => {
        const configs: Record<string, { color: string; bg: string; icon: typeof CheckCircle; label: string }> = {
            confirmed: { color: 'text-blue-600', bg: 'bg-blue-50', icon: Clock, label: 'Confirmed' },
            packed: { color: 'text-purple-600', bg: 'bg-purple-50', icon: Package, label: 'Packed' },
            out_for_delivery: { color: 'text-indigo-600', bg: 'bg-indigo-50', icon: Truck, label: 'Out for Delivery' },
            delivered: { color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle, label: 'Delivered' },
            cancelled: { color: 'text-red-600', bg: 'bg-red-50', icon: XCircle, label: 'Cancelled' },
        };
        return configs[status] || configs.confirmed;
    };

    const handleViewOrder = (orderNumber: string) => {
        navigate(`/orders/${orderNumber}`);
    };

    // Get first item details for preview
    const getFirstItem = (items: OrderItem[] | undefined) => {
        if (!items || items.length === 0) return null;
        return items[0];
    };

    // Get preview text for remaining items count
    const getRemainingCount = (items: OrderItem[] | undefined) => {
        if (!items || items.length <= 1) return null;
        return items.length - 1;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-3 text-gray-500 text-sm">Loading orders...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="max-w-2xl mx-auto px-4 py-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
                        <p className="text-sm text-gray-500">{filteredOrders.length} total</p>
                    </div>
                    <button
                        onClick={(fetchOrders)}
                        className="p-2 text-gray-500 hover:text-gray-700 active:bg-gray-100 rounded-full"
                        aria-label="Refresh"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="relative mb-5">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search by order # or product..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center mb-4">
                        <p className="text-red-700 text-sm">{error}</p>
                        <button onClick={fetchOrders} className="mt-2 text-sm text-red-600 font-medium">Try again</button>
                    </div>
                )}

                {/* Empty State */}
                {!error && filteredOrders.length === 0 && (
                    <div className="text-center py-12">
                        <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">
                            {searchTerm ? 'No matching orders found' : 'No orders yet'}
                        </p>
                        {!searchTerm && (
                            <button
                                onClick={() => navigate('/products')}
                                className="mt-4 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl"
                            >
                                Start Shopping
                            </button>
                        )}
                    </div>
                )}

                {/* Orders List */}
                {!error && filteredOrders.length > 0 && (
                    <div className="space-y-3">
                        {filteredOrders.map((order) => {
                            const statusConfig = getStatusConfig(order.status);
                            const StatusIcon = statusConfig.icon;
                            const firstItem = getFirstItem(order.items);
                            const remainingCount = getRemainingCount(order.items);
                            const orderDate = new Date(order.created_at);
                            const isToday = orderDate.toDateString() === new Date().toDateString();
                            const dateDisplay = isToday ? 'Today' : formatFullDate(order.created_at);
                            return (
                                <div
                                    key={order.order_number}
                                    className="bg-white rounded-xl border border-gray-100 overflow-hidden active:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleViewOrder(order.order_number)}
                                >
                                    <div className="p-4">
                                        {/* Top Row: Order # + Status */}
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <p className="text-xs text-gray-400 font-mono">
                                                    #{order.order_number.slice(-8)}
                                                </p>
                                                <span className="text-xs text-gray-400">
                                                    {dateDisplay}
                                                </span>
                                            </div>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                                                <StatusIcon className="w-3 h-3" />
                                                {statusConfig.label}
                                            </span>
                                        </div>

                                        {/* Product Preview with Larger Image */}
                                        <div className="flex gap-4">
                                            {/* Product Image - Larger */}
                                            <div className="flex-shrink-0">
                                                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-xl overflow-hidden border border-gray-100">
                                                    {firstItem?.product_image ? (
                                                        <img
                                                            src={firstItem.product_image}
                                                            alt={firstItem.product_name || 'Product'}
                                                            className="w-full h-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <Package className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Product Info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 break-words">
                                                    {firstItem?.product_name || 'Product'}
                                                </p>
                                                {firstItem?.product_variant_name && firstItem.product_variant_name !== 'Default' && (
                                                    <p className="text-xs text-gray-500 mt-0.5 break-words">
                                                        {firstItem.product_variant_name}
                                                    </p>
                                                )}
                                                {remainingCount && (
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        + {remainingCount} more item{remainingCount !== 1 ? 's' : ''}
                                                    </p>
                                                )}

                                                {/* Total Price */}
                                                <p className="text-sm font-bold text-gray-900 mt-2">
                                                    {formatPrice(order.total)}
                                                </p>

                                                {/* City (optional) */}
                                                {order.shipping_city_name && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <MapPin className="w-3 h-3 text-gray-400" />
                                                        <span className="text-xs text-gray-500 truncate">
                                                            {order.shipping_city_name}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Chevron */}
                                            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 self-center" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
};

export default MyOrders;